/**
 * Background Manager
 * Handles creation and management of stage backgrounds (gradient, image, video)
 */

import Phaser from 'phaser';
import { BackgroundConfig, ColorGradeConfig, SeaShadeConfig } from '@/types/StageConfig';
import { GAME_CONFIG } from '@/config/constants';

export class BackgroundManager {
  private scene: Phaser.Scene;
  private currentBackground?: Phaser.GameObjects.GameObject | Phaser.GameObjects.Video;
  private currentType?: 'gradient' | 'image' | 'video';

  // AAA: Dual-video crossfade for seamless looping
  private videoA?: Phaser.GameObjects.Video;
  private videoB?: Phaser.GameObjects.Video;
  private activeVideo: 'A' | 'B' = 'A';
  private crossfadeTimer?: Phaser.Time.TimerEvent;

  // Still-image floor drawn BEHIND both video layers (only when the stage
  // supplies a fallback image, e.g. the Hydra arena's boss_bg.jpg). Guarantees
  // the player never sees the black canvas through a buffering / stalling /
  // self-healing video on a poor connection.
  private videoStillFloor?: Phaser.GameObjects.Image;

  // Optional soft overlays over the video (config.seaShade / config.shadowSoften),
  // sit above the video layers so the cinematic bloom and gameplay render correctly.
  private seaShade?: Phaser.GameObjects.Image;
  private shadowSoften?: Phaser.GameObjects.Image;

  // Native HTMLVideoElement listeners we registered so we can remove them on
  // destroyBackground(). Without this, every menu cycle in a 12h kiosk run
  // leaks listeners that keep the video decoders alive.
  private nativeVideoListeners: Array<{ el: HTMLVideoElement; type: string; handler: EventListener }> = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Create background based on configuration
   */
  createBackground(config: BackgroundConfig): void {
    // Destroy existing background if any
    this.destroyBackground();

    console.log(`🎨 Creating ${config.type} background...`);

    switch (config.type) {
      case 'gradient':
        this.createGradientBackground(config);
        break;
      case 'image':
        this.createImageBackground(config);
        break;
      case 'video':
        this.createVideoBackground(config);
        break;
      default:
        console.warn('⚠️ Unknown background type, using default gradient');
        this.createDefaultGradient();
    }

    this.currentType = config.type;
  }

  /**
   * Create gradient background
   */
  private createGradientBackground(config: BackgroundConfig): void {
    const topColor = config.topColor || '#87CEEB';
    const bottomColor = config.bottomColor || '#1E90FF';

    // Create gradient using graphics
    const graphics = this.scene.add.graphics();
    
    // Convert hex to RGB for gradient
    const topRGB = Phaser.Display.Color.HexStringToColor(topColor);
    const bottomRGB = Phaser.Display.Color.HexStringToColor(bottomColor);

    // Create gradient rectangle
    graphics.fillGradientStyle(
      topRGB.color, topRGB.color,
      bottomRGB.color, bottomRGB.color,
      1, 1, 1, 1
    );
    graphics.fillRect(0, 0, GAME_CONFIG.WIDTH, GAME_CONFIG.HEIGHT);
    graphics.setDepth(-100); // Behind everything

    if (config.opacity !== undefined && config.opacity < 1) {
      graphics.setAlpha(config.opacity);
    }

    this.currentBackground = graphics;
    console.log(`✅ Gradient background created: ${topColor} → ${bottomColor}`);
  }

  /**
   * Create image background
   */
  private createImageBackground(config: BackgroundConfig): void {
    if (!config.image) {
      console.error('❌ Image path not provided for image background');
      this.createDefaultGradient();
      return;
    }

    // Use the image filename directly as the texture key
    const textureKey = config.image;
    
    if (!this.scene.textures.exists(textureKey)) {
      console.error(`❌ Background texture '${textureKey}' not loaded`);
      this.createDefaultGradient();
      return;
    }

    // Create image sprite
    const image = this.scene.add.image(
      GAME_CONFIG.WIDTH / 2,
      GAME_CONFIG.HEIGHT / 2,
      textureKey
    );

    // Scale image based on scaleMode
    const scaleX = GAME_CONFIG.WIDTH / image.width;
    const scaleY = GAME_CONFIG.HEIGHT / image.height;
    
    // 'fit' = show entire image (may have letterboxing)
    // 'cover' = fill screen (may crop image)
    const scaleMode = config.scaleMode || 'cover'; // Default 'cover' for images
    const scale = scaleMode === 'cover' ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);
    
    image.setScale(scale);
    image.setDepth(-100);
    
    console.log(`📐 Image: ${image.width}×${image.height}, mode: ${scaleMode}, scale: ${scale.toFixed(2)}`);

    if (config.opacity !== undefined && config.opacity < 1) {
      image.setAlpha(config.opacity);
    }

    this.currentBackground = image;
    console.log(`✅ Image background created: ${config.image}`);
  }

  /**
   * Create video background
   */
  /**
   * AAA: Create video background with seamless crossfade looping.
   * Uses two video instances — when one nears its end, the other fades in
   * from the start, creating a dissolve transition instead of a hard cut.
   */
  private createVideoBackground(config: BackgroundConfig): void {
    if (!config.video) {
      console.error('❌ Video path not provided for video background');
      this.createDefaultGradient();
      return;
    }

    const videoKey = `bg_video_${config.video.replace(/[^a-zA-Z0-9]/g, '_')}`;
    const scaleMode = config.scaleMode || 'cover';
    const opacity = config.opacity ?? 1.0;

    try {
      // ── Still-image floor (anti-flicker safety net) ──────────────────────
      // Lay the stage's fallback image BEHIND both video layers (depth -110,
      // below videoA/-100 and videoB/-101). The streaming video can buffer,
      // stall, or self-heal on a weak connection — states that previously
      // flashed the black canvas through ("black flicker … and another
      // flicker"), most visibly on the boss/Hydra arena. With the approved
      // still arena permanently underneath, the worst a struggling video can
      // do is briefly reveal that still image instead of black. One static
      // sprite; only built when the stage actually provides a fallback image.
      if (config.image && this.scene.textures.exists(config.image)) {
        const floor = this.scene.add.image(GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT / 2, config.image);
        const fsx = GAME_CONFIG.WIDTH / floor.width;
        const fsy = GAME_CONFIG.HEIGHT / floor.height;
        floor.setScale(scaleMode === 'cover' ? Math.max(fsx, fsy) : Math.min(fsx, fsy));
        floor.setDepth(-110);
        this.videoStillFloor = floor;
      }

      // Helper to create and configure a video instance
      const makeVideo = (depth: number): Phaser.GameObjects.Video => {
        const v = this.scene.add.video(
          GAME_CONFIG.WIDTH / 2,
          GAME_CONFIG.HEIGHT / 2,
          videoKey
        );
        v.setDepth(depth);
        v.setMute(true);

        // Assume 1080p by default (matches canvas) so 1080p videos appear
        // UNZOOMED immediately. For 720p content the correction below will
        // bump the scale up — acceptable fallback (brief 1080p-letterbox)
        // vs. the old behaviour (1080p content stuck zoomed 1.5× for up to
        // 15s until the 'playing' event fired on slow loads).
        const safeScale = Math.max(GAME_CONFIG.WIDTH / 1920, GAME_CONFIG.HEIGHT / 1080);
        v.setScale(safeScale);

        // Apply the exact cover/contain scale using the best info we have.
        // Prefers the native HTMLVideoElement (videoWidth/Height available
        // on loadedmetadata — much earlier than Phaser's 'playing').
        const applyCorrectScale = (): boolean => {
          const nativeEl = (v as any).video as HTMLVideoElement | undefined;
          const vw = (nativeEl && nativeEl.videoWidth > 0) ? nativeEl.videoWidth
            : (v.width > 100 ? v.width : 0);
          const vh = (nativeEl && nativeEl.videoHeight > 0) ? nativeEl.videoHeight
            : (v.height > 100 ? v.height : 0);
          if (!vw || !vh) return false;
          const sx = GAME_CONFIG.WIDTH / vw;
          const sy = GAME_CONFIG.HEIGHT / vh;
          const correctScale = scaleMode === 'cover' ? Math.max(sx, sy) : Math.min(sx, sy);
          v.setScale(correctScale);
          v.setData('coverScale', correctScale);
          return true;
        };

        // Immediate attempt (covers the case where metadata is already in cache)
        applyCorrectScale();

        // Phaser-level event (later, but reliable)
        v.on('playing', applyCorrectScale);

        // Native-element events fire FAR earlier than 'playing':
        //   loadedmetadata: videoWidth/videoHeight available
        //   loadeddata:     first frame ready
        const nativeEl = (v as any).video as HTMLVideoElement | undefined;
        if (nativeEl) {
          const handler: EventListener = () => { applyCorrectScale(); };
          nativeEl.addEventListener('loadedmetadata', handler);
          nativeEl.addEventListener('loadeddata', handler);
          nativeEl.addEventListener('canplay', handler);
          // Track so we can remove on destroy. Without this we leak ~3
          // listeners per video per scene cycle.
          this.nativeVideoListeners.push(
            { el: nativeEl, type: 'loadedmetadata', handler },
            { el: nativeEl, type: 'loadeddata', handler },
            { el: nativeEl, type: 'canplay', handler },
          );

          // Self-heal: Chrome can abort a streaming <video> mid-playback with a
          // transient media/cache error — most notably
          // ERR_CACHE_OPERATION_NOT_SUPPORTED on a cold cache, which dropped the
          // menu background ~3-5s into the first-ever load on Windows Chrome and
          // only recovered on a manual refresh. Re-bind the source and replay so
          // the background recovers on its own in ~1s. Capped so a genuinely
          // broken source can't loop forever. Once a video is served from an
          // in-memory blob (see BootScene.blobifyVideo) this never fires; it
          // stays as a safety net for any stream not yet blobbed.
          let healAttempts = 0;
          const selfHeal: EventListener = () => {
            if (!v.scene || !v.active) return; // being torn down — ignore
            if (healAttempts >= 3) {
              console.warn('⚠️ background video media error — self-heal cap reached');
              return;
            }
            healAttempts++;
            console.warn(`⚠️ background video media error — self-heal attempt ${healAttempts}`);
            try {
              v.load(videoKey); // re-request source (blob = instant, network = now warm)
              this.safePlay(v, 'self-heal', config);
            } catch (err) {
              console.warn('⚠️ self-heal reload threw:', err);
            }
          };
          nativeEl.addEventListener('error', selfHeal);
          this.nativeVideoListeners.push({ el: nativeEl, type: 'error', handler: selfHeal });
        }

        // Fallback poll — every 80ms until dimensions resolve, max 5s.
        // Guarantees the scale corrects even if all events somehow miss.
        let pollAttempts = 0;
        const pollTimer = this.scene.time.addEvent({
          delay: 80,
          loop: true,
          callback: () => {
            pollAttempts++;
            if (applyCorrectScale() || pollAttempts > 60) {
              pollTimer.remove();
            }
          },
        });

        return v;
      };

      // Create two video layers
      this.videoA = makeVideo(-100);
      this.videoB = makeVideo(-101); // Behind A initially

      // Optional multiplicative tint (e.g. cool desaturator on a too-vibrant
      // background). String comes in as "0xRRGGBB"; parseInt handles both
      // forms via base 16.
      if (config.tint) {
        const tintNum = typeof config.tint === 'string'
          ? parseInt(config.tint.replace(/^0x/i, ''), 16)
          : config.tint;
        if (!Number.isNaN(tintNum)) {
          this.videoA.setTint(tintNum);
          this.videoB.setTint(tintNum);
        }
      }

      // Optional WebGL color grade (ColorMatrix post filter). Re-lights the
      // background per stage without re-baking the asset — e.g. lifting the
      // world2 beach out of its golden-hour cast toward brighter daylight.
      if (config.colorGrade) {
        this.applyColorGrade(this.videoA, config.colorGrade);
        this.applyColorGrade(this.videoB, config.colorGrade);
      }

      // Optional soft dark ellipse over the sea — drawn above the video so the
      // camera bloom blooms the (now dimmer) water less, taming the reflection.
      if (config.seaShade) {
        this.seaShade = this.createOverlay(config.seaShade, -98, Phaser.BlendModes.NORMAL, 0x000a19);
      }
      // Optional soft warm SCREEN ellipse that lifts the baked tree shadow on
      // the sand so it reads softer (it darkened the foreground a lot).
      if (config.shadowSoften) {
        this.shadowSoften = this.createOverlay(config.shadowSoften, -97, Phaser.BlendModes.SCREEN, 0x6a5230);
      }

      // === STRATEGY: Both videos play simultaneously from the start.
      // B plays invisibly behind A (alpha 0 but rendering).
      // When A nears end, crossfade to B. When B nears end, restart A and crossfade back.
      // No startup latency because both are always warm. ===

      // Both videos loop continuously and stay playing for the lifetime of
      // this background. Visibility is handled by alpha + depth (the standby
      // sits at a lower depth and renders unseen behind the active). Earlier
      // versions paused the standby to save CPU, but Chrome can release the
      // HTMLVideoElement's decoder after ~30s of being paused; when the
      // crossfade then tried to unpause+seek+play it, play() would silently
      // resolve without actually starting playback, and the fade would tween
      // to a black frame. The boss arena (hydra) tripped this most often
      // because the player typically sits on that one scene long enough to
      // hit several crossfade cycles. Always-on loops cost a second
      // hardware-decoded 1080p stream, which any desktop GPU handles easily.
      this.videoA.setAlpha(opacity);
      this.safePlay(this.videoA, 'videoA', config);

      // Start B immediately too. It renders invisibly behind A.
      this.safePlay(this.videoB, 'videoB', config);

      // Playback watchdog: rapid scene restarts (demo mode cycles stages
      // every minute or so) can hand back a Video whose underlying
      // HTMLVideoElement is still warming up when play() is called, and the
      // play promise quietly resolves to nothing. If videoA is not actually
      // playing within 500ms, retry up to a few times. Without this the
      // canvas keeps showing whatever was last rendered (often the previous
      // stage's frozen frame), which surfaces as "the background did not
      // load" during auto demo play.
      let playRetries = 0;
      const verifyPlayback = () => {
        if (!this.videoA || !this.videoA.scene || !this.videoA.active) return;
        if (this.videoA.isPlaying()) return;
        if (playRetries >= 3) {
          console.warn('⚠️ background videoA failed to start after 3 retries, falling back to image');
          // Hard fallback to still image so the player never sees a black canvas.
          this.fallbackToImage(config);
          return;
        }
        playRetries++;
        console.warn(`⚠️ background videoA not playing, retry ${playRetries}`);
        this.safePlay(this.videoA, 'videoA-retry', config);
        this.scene.time.delayedCall(500, verifyPlayback);
      };
      this.scene.time.delayedCall(500, verifyPlayback);

      this.activeVideo = 'A';
      let isCrossfading = false;

      // Poll to detect when active video nears its end
      this.crossfadeTimer = this.scene.time.addEvent({
        delay: 150, // Check ~7x/sec for precision
        loop: true,
        callback: () => {
          if (isCrossfading) return;
          const active = this.activeVideo === 'A' ? this.videoA : this.videoB;
          const standby = this.activeVideo === 'A' ? this.videoB : this.videoA;
          if (!active || !standby) return;

          const duration = active.getDuration();
          const currentTime = active.getCurrentTime();
          if (duration <= 0) return;

          const timeLeft = duration - currentTime;
          // Start crossfade 3 seconds before end (plenty of buffer)
          const fadeWindow = 3.0;
          const fadeDuration = 2000; // 2 second dissolve

          if (timeLeft <= fadeWindow && timeLeft > 0.5) {
            isCrossfading = true;
            console.log(`🎬 Crossfade starting (${timeLeft.toFixed(1)}s left)`);

            // Reset the standby to frame 0. It is already looping (we never
            // pause either video), so no setPaused(false) is needed and there
            // is no decoder to wake up. Apply cached scale first to avoid the
            // zoomed-frame flash that happens before metadata resolves.
            const cachedScale = standby.getData('coverScale');
            if (cachedScale) standby.setScale(cachedScale);
            standby.seekTo(0);
            this.safePlay(standby, 'crossfade-standby', config);
            standby.setAlpha(0);

            // Bring standby in front
            standby.setDepth(-99);
            active.setDepth(-100);

            // Wait 400ms for standby to render its first real frame
            this.scene.time.delayedCall(400, () => {
              // KEY: active stays FULLY VISIBLE underneath — no fade out.
              // Standby fades in ON TOP. Zero black frames possible.
              this.scene.tweens.add({
                targets: standby,
                alpha: opacity,
                duration: fadeDuration,
                ease: 'Sine.easeInOut',
                onComplete: () => {
                  // Hide active now that standby is fully opaque on top.
                  // We deliberately do NOT pause active here; it keeps looping
                  // invisibly so its decoder stays warm and the next crossfade
                  // (when it becomes standby again) plays without a hiccup.
                  active.setAlpha(0);
                  isCrossfading = false;
                },
              });
            });

            // Swap active reference
            this.activeVideo = this.activeVideo === 'A' ? 'B' : 'A';
          }
        },
      });

      this.currentBackground = this.videoA;
      console.log(`✅ Video background created with crossfade loop: ${config.video}`);
    } catch (error) {
      console.error('❌ Failed to create video background:', error);
      this.createDefaultGradient();
    }
  }

  /**
   * Draw a soft elliptical overlay over part of the video. Uses one cached
   * white radial-gradient texture, tinted + alpha'd + blended per call, scaled
   * to the ellipse radii and sat just above the video layers so gameplay and
   * parallax still render on top.
   *
   * Two uses, both config-driven and leaving the source asset untouched:
   *   - seaShade: a NORMAL-blend navy ellipse over the water — darkens it so the
   *     camera bloom doesn't blow out the sun reflection.
   *   - shadowSoften: a SCREEN-blend warm ellipse over the front-right tree
   *     shadow — lifts the baked-in shadow on the sand so it reads softer.
   */
  private createOverlay(cfg: SeaShadeConfig, depth: number, blendMode: number, defaultColor: number): Phaser.GameObjects.Image | undefined {
    try {
      const key = 'bg_overlay_gradient';
      if (!this.scene.textures.exists(key)) {
        const size = 256;
        const tex = this.scene.textures.createCanvas(key, size, size);
        const ctx = tex?.getContext();
        if (tex && ctx) {
          const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
          grad.addColorStop(0, 'rgba(255,255,255,1)');
          grad.addColorStop(0.55, 'rgba(255,255,255,0.55)');
          grad.addColorStop(1, 'rgba(255,255,255,0)');
          ctx.fillStyle = grad;
          ctx.fillRect(0, 0, size, size);
          tex.refresh();
        }
      }
      const img = this.scene.add.image(cfg.cx, cfg.cy, key).setDepth(depth);
      img.setDisplaySize(cfg.rx * 2, cfg.ry * 2);
      img.setAlpha(cfg.alpha);
      img.setBlendMode(blendMode);
      const tintNum = cfg.color
        ? parseInt(cfg.color.replace(/^0x/i, ''), 16)
        : defaultColor;
      if (!Number.isNaN(tintNum)) img.setTint(tintNum);
      return img;
    } catch (e) {
      console.warn('⚠️ background overlay failed:', e);
      return undefined;
    }
  }

  /**
   * Apply a ColorMatrix post filter to a video layer (Phaser 4 Filters API).
   * Composes brightness, saturation and a per-channel RGB multiply so a
   * background can be re-graded purely from stage config, leaving the source
   * asset byte-for-byte untouched. Guarded so a renderer without the filter
   * API (or a non-WebGL fallback) silently keeps the ungraded video.
   */
  private applyColorGrade(video: Phaser.GameObjects.Video, grade: ColorGradeConfig): void {
    const v = video as any;
    if (typeof v.enableFilters !== 'function') return;
    try {
      v.enableFilters();
      const ctrl = v.filters?.internal?.addColorMatrix?.();
      const cm = ctrl?.colorMatrix;
      if (!cm) return;
      cm.reset();
      if (grade.brightness != null && grade.brightness !== 1) cm.brightness(grade.brightness, true);
      if (grade.saturate != null && grade.saturate !== 0) cm.saturate(grade.saturate, true);
      const r = grade.rMultiply ?? 1;
      const g = grade.gMultiply ?? 1;
      const b = grade.bMultiply ?? 1;
      if (r !== 1 || g !== 1 || b !== 1) {
        cm.multiply([
          r, 0, 0, 0, 0,
          0, g, 0, 0, 0,
          0, 0, b, 0, 0,
          0, 0, 0, 1, 0,
        ], true);
      }
    } catch (e) {
      console.warn('⚠️ color grade failed:', e);
    }
  }

  /**
   * Try to play a Phaser Video object. If play() returns a Promise (HTMLVideoElement
   * does), catch any rejection so an autoplay-policy denial does not leave the
   * background black. Rejection is logged once; the watchdog retry loop will
   * either recover or escalate to the still-image fallback.
   */
  private safePlay(video: Phaser.GameObjects.Video, tag: string, config: BackgroundConfig): void {
    try {
      video.play(true);
      // Phaser's Video.play() resolves on its own promise chain; we also reach
      // into the native element to catch a direct play() rejection (autoplay
      // policy, decoder error). Some browsers surface failures only this way.
      const nativeEl = (video as any).video as HTMLVideoElement | undefined;
      const p = nativeEl?.play?.();
      if (p && typeof p.catch === 'function') {
        p.catch((err: unknown) => {
          console.warn(`⚠️ background ${tag} native play() rejected:`, err);
          // The watchdog (verifyPlayback) will see isPlaying()===false and retry.
          // If retries also fail, fallbackToImage() kicks in there.
          // For the crossfade-standby case, fall back here too because there is
          // no watchdog on the standby.
          if (tag === 'crossfade-standby') {
            this.fallbackToImage(config);
          }
        });
      }
    } catch (err) {
      console.warn(`⚠️ background ${tag} play() threw:`, err);
    }
  }

  /**
   * Hard fallback: tear down the video layers and put up a still image (or a
   * gradient) so the player never sees a black canvas. Used when video
   * playback cannot be coaxed into starting after retries.
   */
  private fallbackToImage(config: BackgroundConfig): void {
    console.warn('🛟 Falling back to still image background');
    // Stop and destroy video layers cleanly.
    if (this.crossfadeTimer) {
      this.crossfadeTimer.remove();
      this.crossfadeTimer = undefined;
    }
    this.removeNativeVideoListeners();
    if (this.videoA) { try { this.videoA.stop(); this.videoA.destroy(); } catch {} this.videoA = undefined; }
    if (this.videoB) { try { this.videoB.stop(); this.videoB.destroy(); } catch {} this.videoB = undefined; }
    // Drop the still-image floor too; createImageBackground() below will build
    // the canonical full-screen still as currentBackground (avoids two stacked
    // copies of the same image).
    if (this.videoStillFloor) { this.videoStillFloor.destroy(); this.videoStillFloor = undefined; }
    this.currentBackground = undefined;
    this.currentType = undefined;

    // Prefer a config-provided still image if the stage has one.
    if (config.image) {
      this.createImageBackground({ ...config, type: 'image' });
      return;
    }
    this.createDefaultGradient();
  }

  private removeNativeVideoListeners(): void {
    for (const { el, type, handler } of this.nativeVideoListeners) {
      try { el.removeEventListener(type, handler); } catch {}
    }
    this.nativeVideoListeners.length = 0;
  }

  /**
   * Create default gradient background (fallback)
   */
  private createDefaultGradient(): void {
    const graphics = this.scene.add.graphics();
    graphics.fillGradientStyle(
      0x87CEEB, 0x87CEEB,
      0x1E90FF, 0x1E90FF,
      1, 1, 1, 1
    );
    graphics.fillRect(0, 0, GAME_CONFIG.WIDTH, GAME_CONFIG.HEIGHT);
    graphics.setDepth(-100);
    this.currentBackground = graphics;
    console.log('✅ Default gradient background created');
  }

  /**
   * Smoothly crossfade from the current background to a new one — used for
   * narrative transitions (e.g. "the ocean is cleansed" after boss defeat).
   *
   * Layers the new background OVER the current one at alpha 0, warms it up,
   * then tweens its alpha to full. When opaque, the old background is torn
   * down. The old background's crossfade-loop timer is stopped so the old
   * video doesn't fade-swap itself under the new one during the transition.
   *
   * Returns a Promise that resolves when the crossfade completes.
   */
  async crossfadeTo(config: BackgroundConfig, durationMs: number = 3000): Promise<void> {
    console.log(`🌊 Crossfading background → ${config.type}${config.video ? ':' + config.video : config.image ? ':' + config.image : ''}`);

    // Freeze the outgoing crossfade-loop so the old video stays static underneath
    if (this.crossfadeTimer) {
      this.crossfadeTimer.remove();
      this.crossfadeTimer = undefined;
    }

    // Stash the previous background/videos so we can fade them out + destroy
    const oldBackground = this.currentBackground;
    const oldType = this.currentType;
    const oldVideoA = this.videoA;
    const oldVideoB = this.videoB;

    // Detach so createBackground() doesn't nuke them immediately
    this.currentBackground = undefined;
    this.videoA = undefined;
    this.videoB = undefined;

    // Build the new background at its normal depth (-100). We'll bring it
    // above the old one temporarily so the fade layers correctly.
    this.createBackground(config);

    // Raise whichever new layers exist above the old ones
    const raise = (obj?: Phaser.GameObjects.GameObject) => {
      if (obj && (obj as any).setDepth) (obj as any).setDepth(-50);
    };
    raise(this.currentBackground as any);
    raise(this.videoA as any);
    raise(this.videoB as any);

    // Start the new fully transparent
    const newLayers: Phaser.GameObjects.GameObject[] = [];
    if (this.videoA) { (this.videoA as any).setAlpha(0); newLayers.push(this.videoA); }
    if (this.videoB) { (this.videoB as any).setAlpha(0); newLayers.push(this.videoB); }
    if (this.currentBackground && this.currentType !== 'video') {
      (this.currentBackground as any).setAlpha?.(0);
      newLayers.push(this.currentBackground);
    }

    // Give the new video ~400ms to warm up before starting the fade
    const warmupMs = config.type === 'video' ? 400 : 0;

    return new Promise<void>((resolve) => {
      this.scene.time.delayedCall(warmupMs, () => {
        const targetAlpha = config.opacity ?? 1;
        this.scene.tweens.add({
          targets: newLayers,
          alpha: targetAlpha,
          duration: durationMs,
          ease: 'Sine.easeInOut',
          onComplete: () => {
            // Tear down old background after the new one is fully opaque
            try { oldVideoA?.stop(); oldVideoA?.destroy(); } catch {}
            try { oldVideoB?.stop(); oldVideoB?.destroy(); } catch {}
            if (oldBackground && oldBackground !== this.currentBackground) {
              try {
                if (oldType === 'video' && 'stop' in oldBackground) {
                  (oldBackground as Phaser.GameObjects.Video).stop();
                }
                oldBackground.destroy();
              } catch {}
            }
            // Return new layers to normal depth
            const normalize = (obj?: Phaser.GameObjects.GameObject) => {
              if (obj && (obj as any).setDepth) (obj as any).setDepth(-100);
            };
            normalize(this.currentBackground as any);
            normalize(this.videoA as any);
            normalize(this.videoB as any);
            resolve();
          },
        });
      });
    });
  }

  /**
   * Destroy current background
   */
  destroyBackground(): void {
    // Clean up crossfade system
    if (this.crossfadeTimer) {
      this.crossfadeTimer.remove();
      this.crossfadeTimer = undefined;
    }
    // Detach native HTMLVideoElement listeners BEFORE destroying the videos so
    // we don't leak listeners across kiosk play cycles. Without this, ~6
    // listeners per scene cycle accumulate (3 events × 2 videos) and over a
    // 12h run can build into the tens of thousands, holding video element
    // refs and slowly degrading performance.
    this.removeNativeVideoListeners();
    if (this.videoA) { this.videoA.stop(); this.videoA.destroy(); this.videoA = undefined; }
    if (this.videoB) { this.videoB.stop(); this.videoB.destroy(); this.videoB = undefined; }
    if (this.seaShade) { this.seaShade.destroy(); this.seaShade = undefined; }
    if (this.shadowSoften) { this.shadowSoften.destroy(); this.shadowSoften = undefined; }
    if (this.videoStillFloor) { this.videoStillFloor.destroy(); this.videoStillFloor = undefined; }

    if (this.currentBackground) {
      if (this.currentType === 'video' && 'stop' in this.currentBackground) {
        (this.currentBackground as Phaser.GameObjects.Video).stop();
      }
      this.currentBackground.destroy();
      this.currentBackground = undefined;
      this.currentType = undefined;
    }
  }

  /**
   * Get background asset key from path
   */
  static getAssetKey(path: string, type: 'image' | 'video'): string {
    return `bg_${type}_${path.replace(/[^a-zA-Z0-9]/g, '_')}`;
  }
}
